import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Alert,
  FormControl,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Box,
  Button,
  Switch,
  Stack,
  Autocomplete,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { AddPlusIcon } from '../../../vectors/index';
import Add from '@mui/icons-material/Add';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateTemplate } from '../hooks/useCreateTemplate';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import { authContext } from '@staffedupusa/shared-components';
import { mapPlanStatusAndColor } from '@utils/companyHelper';
import { getStrippedText } from '@utils/helper';

type AddTemplateModalProps = {
  handleClose: () => void;
};

const schema = yup
  .object({
    templateName: yup
      .string()
      .required('Name is required')
      .test('not-empty-html', 'Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    templateDescription: yup
      .string()
      .required('Description is required')
      .test('not-empty-html', 'Description cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
  })
  .required();

const AddTemplateModal: FC<AddTemplateModalProps> = ({ handleClose }) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');
  const [selection, setSelection] = useState<string>('');
  const [billing, setBilling] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<number | string>('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [applyChanges, setApplyChanges] = useState(false);
  const [companiesData, setCompaniesData] = useState<any[]>([]);

  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  useEffect(() => {
    if (metricsData?.companies) {
      const updatedCompaniesData = mapPlanStatusAndColor(metricsData.companies);
      setCompaniesData(updatedCompaniesData);
    }
    if (metricsData?.companies && metricsData?.companies?.length > 0) {
      setShowTemplateForm(false);
    } else {
      setSelection('new');
      setShowTemplateForm(true);
    }
  }, [metricsData]);

  const { createTemplate, isCreatingTemplate } = useCreateTemplate({
    setErrorText,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleAdd = (data: {
    templateName: string;
    templateDescription: string;
  }) => {
    const options = {
      onSuccess: (data: { message: string; status: number }) => {
        if (data.status === 200) {
          toast.success(data.message);
          handleClose();
        }
      },
    };
    createTemplate(
      {
        template_name: data.templateName,
        template_description: data.templateDescription,
        apply_changes: applyChanges,
        payment_method: Number(billing),
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        ...(selectedCompany ? { status: 1 } : {}),
      },
      options,
    );
  };

  const handleNext = () => {
    if (selection === 'existing') {
      setShowTemplateForm(true);
    } else {
      setShowTemplateForm(true);
    }
  };
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setApplyChanges(checked);
  };
  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <AddPlusIcon />
        <br />
        <Typography variant="h4">
          Add Template
          {selectedCompany &&
            ` by ${companiesData.find((company) => company.company_id === selectedCompany)?.name}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert sx={{ margin: '10px 0' }} severity="error">
            {errorText}
          </Alert>
        )}
        {!showTemplateForm && (
          <>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="template-option"
                name="templateOption"
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Create New Template"
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Create from Existing Company"
                />
              </RadioGroup>
            </FormControl>
            <Button
              fullWidth
              sx={{ marginTop: 2, marginBottom: 2 }}
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleNext}
              disabled={!selection}
            >
              Next
            </Button>
          </>
        )}
        {showTemplateForm && selection === 'existing' && (
          <>
            <FormControl fullWidth sx={{ marginBottom: '10px' }}>
              <Typography id="pay-rate-type-label" sx={{ mb: 0.5 }}>
                Select Company
              </Typography>
              <Autocomplete
                options={companiesData}
                getOptionLabel={(option) => option?.name || ''}
                value={companiesData.find(c => c.company_id === selectedCompany) || null}
                onChange={(_, newValue) => setSelectedCompany(newValue?.company_id || '')}
                isOptionEqualToValue={(option, value) => option.company_id === value.company_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select Company"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.company_id}>
                    {option.name}
                    <span
                      style={{
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: 600,
                        backgroundColor: option.color,
                        marginLeft: '10px',
                        padding: '1px 5px 2px',
                        borderRadius: 5,
                      }}
                    >
                      {option.plan_status}
                    </span>
                  </li>
                )}
              />
            </FormControl>
          </>
        )}
        {showTemplateForm && (selection === 'new' || selectedCompany) && (
          <form onSubmit={handleSubmit(handleAdd)}>
            <FormControl sx={{ marginBottom: '10px' }} fullWidth>
              <Typography id="pay-rate-type-label">Name</Typography>
              <Controller
                name="templateName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    variant="outlined"
                    error={!!errors.templateName}
                    helperText={errors.templateName?.message}
                    fullWidth
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ marginBottom: '10px' }} fullWidth>
              <Typography id="pay-rate-type-label">Description</Typography>
              <Controller
                name="templateDescription"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    variant="outlined"
                    error={!!errors.templateDescription}
                    helperText={errors.templateDescription?.message}
                    fullWidth
                    multiline
                    rows={4}
                    maxRows={4}
                  />
                )}
              />
            </FormControl>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="template-billing"
                name="payment_method"
                value={billing}
                onChange={(e) => setBilling(e.target.value)}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label="Bill to Enterprise"
                />
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label="Bill to Individual Employer"
                />
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset">
              <Stack
                direction="row"
                spacing={1}
                alignItems="start"
                sx={{ mt: 2, marginLeft: '-10px', mb: 2 }}
              >
                <Switch
                  onChange={handleSwitchChange}
                  size="medium"
                  checked={applyChanges}
                />
                <Box mb={2}>
                  <Typography sx={{ whiteSpace: 'nowrap' }}>
                    Global Edits
                  </Typography>
                  <Typography
                    color="gray"
                    sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                  >
                    Future edits to this template will {!applyChanges && 'not'}{' '}
                    affect {applyChanges && 'all'} companies attached.
                  </Typography>
                </Box>
              </Stack>
            </FormControl>
            <LoadingButton
              loading={isCreatingTemplate}
              disabled={isCreatingTemplate || !billing}
              fullWidth
              startIcon={<Add />}
              variant="contained"
              color="primary"
              type="submit"
              sx={{ marginTop: 2, marginBottom: 2 }}
            >
              Add Template
            </LoadingButton>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddTemplateModal;
